"use client";

/**
 * MenuLanguageSelector — read this menu in your own language.
 *
 * TWO THINGS WERE WRONG HERE.
 *
 * 1. "English" was in the list but could not be reached. The translation hook
 *    treated `en` as the do-nothing option — picking it cleared the translation
 *    and showed the menu exactly as stored. That is only right if every menu is
 *    written in English, and nothing in the schema records what language a menu
 *    was authored in. On a French-authored menu a guest could pick English,
 *    watch the flag change, and go on reading French. "Show the original" is now
 *    its own row at the top of the list, and English is a translation target
 *    like every other language. See ORIGINAL_LANGUAGE in useMenuTranslation.
 *
 * 2. The list could not be scrolled. Fifteen languages is ~510px of rows in a
 *    panel with no height limit, so on a phone the bottom of the list — Arabic,
 *    Dutch, Chinese — rendered past the fold with nothing to scroll. The panel
 *    is now capped and scrolls, and it flips above the button when there is more
 *    room up there than down.
 */

import * as React from "react";
import { Check, ChevronDown, Globe, Loader2 } from "lucide-react";
import { cn } from "@/components/ui/utils";
import { zClass } from "@/lib/ui/z-layers";
import { useTranslation } from "@/hooks/useTranslation";
import { LANGUAGES, ORIGINAL_LANGUAGE, isOriginalLanguage } from "@/hooks/menu/useMenuTranslation";

interface MenuLanguageSelectorProps {
  selectedLanguage: string;
  onSelect: (code: string) => void;
  isTranslating?: boolean;
  /**
   * Header mode: a 40px round icon button matching Search and Account beside
   * it, with no text label or chevron. The full form (flag + language name)
   * is for surfaces with room to spare.
   */
  compact?: boolean;
}

export function MenuLanguageSelector({
  selectedLanguage,
  onSelect,
  isTranslating,
  compact = false,
}: MenuLanguageSelectorProps) {
  const { t } = useTranslation(selectedLanguage);
  const [open, setOpen] = React.useState(false);
  const [dropUp, setDropUp] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close on Escape — the panel covers most of a phone screen, and a guest who
  // opened it by accident needs a way out that is not "guess where the edge is".
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const showing = isOriginalLanguage(selectedLanguage)
    ? null
    : LANGUAGES.find((l) => l.code === selectedLanguage) ?? null;

  const openPanel = () => {
    // Flip upward when the button sits low enough that a downward panel would
    // run off the screen — which is exactly where it sits inside a sheet or on
    // a short viewport.
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setDropUp(window.innerHeight - rect.bottom < 280 && rect.top > 280);
    setOpen((value) => !value);
  };

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={openPanel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.selector_tooltip")}
        title={t("lang.selector_tooltip")}
        className={cn(
          "flex items-center transition-colors",
          "outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]",
          compact
            ? // Matches the header's other icon buttons exactly, so the three
              // read as one set rather than as a control plus two icons. 44px,
              // the floor doc 05 §5.4 sets for a touch surface.
              "size-11 shrink-0 justify-center rounded-full text-[var(--color-text-primary)] hover:bg-[var(--color-bg-surface-hover)]"
            : "h-11 gap-1.5 rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-surface-hover)]",
        )}
      >
        {isTranslating ? (
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        ) : showing ? (
          // A chosen language shows its flag: the guest can confirm at a glance
          // what they are reading without opening anything.
          <span className={compact ? "text-lg leading-none" : "text-sm leading-none"}>{showing.flag}</span>
        ) : (
          // On the original, a globe — not a flag. A flag here would claim the
          // menu is written in that country's language, which is the very
          // assumption that made English unreachable.
          <Globe className="size-5" aria-hidden="true" />
        )}

        {!compact ? (
          <>
            <span className="hidden sm:inline">{showing?.native ?? "Original"}</span>
            <ChevronDown className="hidden size-3 sm:inline" aria-hidden="true" />
          </>
        ) : null}
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("lang.selector_tooltip")}
          className={cn(
            "absolute right-0 w-56 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-1 shadow-xl",
            zClass("sheet"),
            // Capped and scrollable: the list is longer than a phone screen.
            "max-h-[min(60vh,20rem)] overflow-y-auto overscroll-contain",
            dropUp ? "bottom-full mb-2" : "top-full mt-2",
          )}
        >
          <LanguageRow
            flag={null}
            native="Original"
            label="as the menu was written"
            selected={isOriginalLanguage(selectedLanguage)}
            onClick={() => {
              onSelect(ORIGINAL_LANGUAGE);
              setOpen(false);
            }}
          />

          <div className="my-1 h-px bg-[var(--color-border-subtle)]" aria-hidden="true" />

          {LANGUAGES.map((lang) => (
            <LanguageRow
              key={lang.code}
              flag={lang.flag}
              native={lang.native}
              label={lang.label}
              selected={lang.code === selectedLanguage}
              onClick={() => {
                onSelect(lang.code);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LanguageRow({
  flag,
  native,
  label,
  selected,
  onClick,
}: {
  flag: string | null;
  native: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-xs transition-colors",
        "outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]",
        selected
          ? "bg-[var(--color-accent)]/10 font-medium text-[var(--color-accent)]"
          : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-sunken)]",
      )}
    >
      <span className="w-5 shrink-0 text-center text-base leading-none">
        {flag ?? <Globe className="mx-auto size-4" aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-medium">{native}</span>
        <span className="ml-1.5 text-[10px] text-[var(--color-text-secondary)]">{label}</span>
      </span>
      {selected && <Check className="size-3.5 shrink-0" aria-hidden="true" />}
    </button>
  );
}
